// src/hooks/useAppEffects.ts
import { useEffect, useRef } from 'react'; // Added useRef
import { Editor } from '@tiptap/react';
import { CoreState } from './useCoreState';
import { ChapterState } from './useChapterState';
import { ProjectManagementControls } from './useProjectManagement';
import { MainView } from '../types';
import { useChapterContentLoader } from './useChapterContentLoader';

export const useAppEffects = (
    coreState: CoreState,
    chapterState: ChapterState,
    projectManagement: ProjectManagementControls,
    determineInitialViewBasedOnState: (currentProjectState: CoreState['projectState'], currentBlueprintReady: boolean) => MainView
) => {
    const { editor, activeVersion, mainView, isInitialBlueprintReady, activeChapter, projectState, setMainView, setProjectState } = coreState;
    const { setActiveChapterDetailEdits } = chapterState; // Only need setter here
    
    // Initialize cache-first chapter content loader
    const { loadChapterContent } = useChapterContentLoader();

    // Effect for editor content and editable status
    useEffect(() => {
        console.log('🔍 useAppEffects triggered with:', {
            hasEditor: !!editor,
            hasActiveChapter: !!activeChapter,
            chapterStatus: activeChapter?.status,
            hasActiveVersion: !!activeVersion,
            activeVersionContent: activeVersion?.content?.substring(0, 50) + '...',
            chapterHistoryLength: activeChapter?.history?.length || 0
        });
        
        // Early return if no activeChapter
        if (!activeChapter) return;
        
        if (editor && activeChapter) {
            // Handle content loading for chapters with text_generated status but no activeVersion
            if (activeChapter.status === 'text_generated' && (activeChapter.history?.length ?? 0) === 0) {
                
                // Load content using cache-first strategy
                const loadPersistedContent = async () => {
                    try {
                        // Use cache-first loader with project ID
                        const projectId = projectState?.projectId || projectState?.currentProjectName || 'unknown';
                        const result = await loadChapterContent(projectId, activeChapter.id);
                        
                        if (result.content) {
                            
                            // Use existing version history if available from cache, otherwise create new
                            const versionHistory = result.versionHistory.length > 0 
                                ? result.versionHistory 
                                : [{
                                    content: result.content,
                                    generatedSummary: 'Generated chapter content',
                                    timestamp: Date.now()
                                  }];
                            
                            // Update project state to include version history
                            setProjectState(prev => ({
                                ...prev!,
                                chapters: (prev?.chapters ?? []).map(chapter => 
                                    chapter.id === activeChapter.id 
                                        ? { 
                                            ...chapter, 
                                            history: versionHistory,
                                            historyIndex: versionHistory.length - 1 // Point to the latest version
                                          }
                                        : chapter
                                )
                            }));
                            
                            // Set editor content
                            if (editor.getHTML() !== result.content) {
                                editor.commands.setContent(result.content, false);
                            }
                        } else {
                            // No content available, show placeholder
                            if (editor.getHTML() !== '<p>No content available...</p>') {
                                editor.commands.setContent('<p>No content available...</p>', false);
                            }
                        }
                    } catch (error) {
                        console.error('Failed to load persisted chapter content:', error);
                        // Fallback to placeholder if loading fails
                        if (editor.getHTML() !== '<p>Loading content...</p>') {
                            editor.commands.setContent('<p>Loading content...</p>', false);
                        }
                    }
                };
                loadPersistedContent();
            } else {
                // Standard content loading for chapters with activeVersion or other states
                // CRITICAL FIX: Show preview content when no active version exists
                let newContent;
                if (activeVersion?.content) {
                    newContent = activeVersion.content;
                } else if (activeChapter?.previewContent && activeChapter.previewContent.length > 0) {
                    newContent = activeChapter.previewContent;
                } else {
                    newContent = '<p>Start writing or generate content...</p>';
                }

                if (editor.getHTML() !== newContent) {
                    editor.commands.setContent(newContent, false);
                }
            }
            
            const isEditable = mainView === 'chaptersBuilding' &&
                               isInitialBlueprintReady &&
                               !!activeChapter &&
                               ((activeChapter?.history?.length ?? 0) > 0 || activeChapter?.status === 'text_generated');
            if (editor.isEditable !== isEditable) {
                editor.setEditable(isEditable);
            }
        }
    }, [editor, activeVersion, activeChapter, mainView, isInitialBlueprintReady]);

    // Effect for determining mainView ON PROJECT LOAD/NEW or RESET
    useEffect(() => {
        // ---- SAFETY GUARDS ---------------------------------------------
        if (!projectState) return;                       // still hydrating
        const savedProjects = Array.isArray(projectManagement?.savedProjects)
            ? projectManagement.savedProjects
            : [];
        
        if (savedProjects.length === 0) return;          // nothing to decide yet
        
        const loadedProject = savedProjects.find(p => p.projectName === projectState?.currentProjectName);
        // ----------------------------------------------------------------

        if (loadedProject && !projectState?.isUnsaved) {
            // A project is loaded and considered "stable"
            const correctViewForLoadedState = determineInitialViewBasedOnState(projectState, isInitialBlueprintReady);
            // Only set if it's different to prevent potential loops if other deps trigger it
            if (mainView !== correctViewForLoadedState) {
                 setMainView(correctViewForLoadedState);
            }
        } else if (!projectState?.currentProjectName && !projectState?.outlineIdea && !isInitialBlueprintReady) {
            // This handles the case of a new, empty project, or project reset
            if (mainView !== 'ideaHub') {
                setMainView('ideaHub');
            }
        }
    }, [
        projectState?.currentProjectName, // This changing signifies a new project context
        projectState?.isUnsaved,          // Ensures we act on a "settled" load/save
        projectState?.outlineIdea,        // For the empty/reset case
        isInitialBlueprintReady,         // Critical for determining the correct view
        // projectManagement.savedProjects, // Not strictly needed if currentProjectName is main trigger
        setMainView,
        determineInitialViewBasedOnState
        // CRUCIALLY, mainView is NOT in this dependency array.
        // Also removed editor, activeVersion, projectState.activeChapterId, projectState.chapters from deps
        // as they are not primary triggers for *initial view determination* after load/new.
        // The editor sync part was removed as it's handled by the first effect.
    ]);
};